import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  Download,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, absoluteUrl, getOrderToken, track } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const StatusRow = ({ done, active, label }) => (
  <div className="flex items-center gap-3 py-3">
    <span
      className={`inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
        done
          ? "bg-emerald-100 text-emerald-600"
          : active
          ? "bg-blue-100 text-blue-600"
          : "bg-slate-100 text-slate-300 dark:bg-slate-800 dark:text-slate-600"
      }`}
    >
      {done ? (
        <CheckCircle2 className="w-5 h-5" />
      ) : active ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <div className="w-2 h-2 rounded-full bg-current" />
      )}
    </span>
    <span className={`text-sm font-medium ${done || active ? "text-slate-900 dark:text-white" : "text-slate-400"}`}>
      {label}
    </span>
  </div>
);

export default function Success() {
  useSEO("Plata confirmată | ActeSimplu");
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef(null);
  const tracked = useRef(false);

  useEffect(() => {
    const token = getOrderToken(orderId);
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        if (sessionId) {
          await api.get(`/payments/status/${sessionId}`).catch(() => {});
        }
        if (token) {
          const r = await api.get(`/orders/${orderId}?token=${token}`);
          setOrder(r.data);
          if (r.data.payment_status === "paid" && !tracked.current) {
            tracked.current = true;
            track("payment_success", { order_id: orderId });
          }
          if (r.data.generation_status === "completed" || r.data.generation_status === "failed") {
            return; // stop polling
          }
        } else {
          const r = await api.get(`/payments/status/${sessionId}`);
          setOrder({
            payment_status: r.data.payment_status,
            generation_status: r.data.generation_status,
            downloads: {},
            no_token: true,
          });
          if (r.data.generation_status === "completed" || r.data.generation_status === "failed") return;
        }
      } catch (e) {
        if (attempts > 3) setFailed(true);
      }
      if (attempts < 40) {
        timerRef.current = setTimeout(poll, 2500);
      }
    };
    poll();
    return () => clearTimeout(timerRef.current);
  }, [orderId, sessionId]);

  const paid = order?.payment_status === "paid";
  const genStatus = order?.generation_status;
  const completed = genStatus === "completed";
  const genFailed = genStatus === "failed";
  const downloads = order?.downloads || {};

  const dl = (fmt) => {
    if (!downloads[fmt]) return;
    track(fmt === "pdf" ? "download_pdf" : "download_docx", { order_id: orderId });
    window.open(absoluteUrl(downloads[fmt]), "_blank");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto py-14 sm:py-20">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 mb-5">
          {paid ? <CheckCircle2 className="w-8 h-8" /> : <Loader2 className="w-8 h-8 animate-spin" />}
        </span>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
          {paid ? "Plata a fost confirmată." : "Se confirmă plata..."}
        </h1>
        <p className="mt-2 text-slate-500 dark:text-slate-400">
          {completed
            ? "Documentul tău este gata de descărcare."
            : "Documentul tău este în curs de generare."}
        </p>
      </motion.div>

      <div className="mt-10 p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          <StatusRow done={paid} active={!paid} label="Plata confirmată" />
          <StatusRow
            done={completed}
            active={paid && !completed && !genFailed}
            label="Document generat"
          />
          <StatusRow done={completed} active={false} label="PDF și Word pregătite" />
        </div>

        {completed && !order?.no_token && (
          <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="download-section">
            <Button
              data-testid="download-pdf-btn"
              onClick={() => dl("pdf")}
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12"
            >
              <Download className="w-4 h-4 mr-2" /> Descarcă PDF
            </Button>
            <Button
              data-testid="download-docx-btn"
              onClick={() => dl("docx")}
              variant="outline"
              className="rounded-full font-semibold h-12 border-slate-300"
            >
              <FileText className="w-4 h-4 mr-2" /> Descarcă Word
            </Button>
          </div>
        )}

        {completed && order?.no_token && (
          <div className="mt-6 p-4 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-sm text-slate-700 dark:text-slate-200">
            Documentul a fost generat și trimis pe email. Verifică-ți inboxul pentru
            linkurile de descărcare.
          </div>
        )}

        {genFailed && (
          <div className="mt-6 p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {order?.error_message ||
                "Nu am putut genera documentul momentan. Comanda ta este înregistrată și procesarea va fi reluată automat."}
            </p>
          </div>
        )}

        {paid && !completed && !genFailed && (
          <p className="mt-6 text-center text-xs text-slate-400">
            Generarea durează de obicei câteva secunde. Această pagină se
            actualizează automat.
          </p>
        )}
      </div>

      {failed && !order && (
        <p className="mt-6 text-center text-sm text-red-500">
          Nu am putut încărca statusul comenzii. Reîncarcă pagina.
        </p>
      )}

      <div className="mt-8 text-center">
        <Link to="/" className="text-sm text-slate-500 hover:text-blue-600">
          Înapoi la pagina principală
        </Link>
      </div>
    </div>
  );
}
